import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";

import { getCategories } from "../services/categoryService";
import {
  addSubCategory,
  updateSubCategory,
  getSubCategoryById,
} from "../services/subCategoryService";

import "./AddOutletLayer.css";

const AddSubCategoryLayer = () => {
  const { id } = useParams(); // ✅ IMPORTANT
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    category_id: "",
    sub_category_name: "",
  });

  const [errors, setErrors] = useState({});

  /* ================= LOAD ================= */
  useEffect(() => {
    loadCategories();
    if (isEdit) loadSubCategory();
  }, [id]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      if (res?.status === "success") {
        setCategories(res.categories || []);
      }
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOAD EDIT DATA ================= */
  const loadSubCategory = async () => {
    try {
      const res = await getSubCategoryById(id);

      if (res?.status === "success") {
        const data = res.data;

        setForm({
          category_id: data.category_id ?? "",
          sub_category_name: data.sub_category_name ?? "",
        });
      } else {
        toast.error("Failed to load sub category");
      }
    } catch {
      toast.error("API Error");
    }
  };

  /* ================= CHANGE ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  /* ================= VALIDATE ================= */
  const validateForm = () => {
    let temp = {};

    if (!form.category_id)
      temp.category_id = "Please select category";

    if (!form.sub_category_name.trim())
      temp.sub_category_name = "Sub category name is required";

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const res = isEdit
        ? await updateSubCategory(id, form)
        : await addSubCategory(form);

      if (res?.status === "success") {
        toast.success(
          isEdit ? "Sub Category Updated" : "Sub Category Added"
        );
        navigate("/sub-category-list");
      } else {
        toast.error(res.message || "Failed");
      }
    } catch {
      toast.error("API Error");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="container mt-3 mb-5">
      <div className="addoutlet-wrapper">
        <h4 className="addoutlet-title">
          {isEdit ? "Update Sub Category" : "Add Sub Category"}
        </h4>

        <form onSubmit={handleSubmit}>
          {/* CATEGORY */}
          <div className="sf-field full">
            <label>Category *</label>
            <select
              className="addoutlet-select"
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Select Category --</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            {errors.category_id && (
              <p className="addoutlet-error">{errors.category_id}</p>
            )}
          </div>

          {/* SUB CATEGORY NAME */}
          <div className="sf-field full">
            <label>Sub Category Name *</label>
            <input
              className="addoutlet-input"
              name="sub_category_name"
              value={form.sub_category_name}
              onChange={handleChange}
            />
            {errors.sub_category_name && (
              <p className="addoutlet-error">
                {errors.sub_category_name}
              </p>
            )}
          </div>

          {/* BUTTONS */}
          <div className="d-flex gap-3 mt-4">
            <button className="addoutlet-btn-primary">
              {isEdit ? "Update Sub Category" : "Add Sub Category"}
            </button>

            <Link
              to="/sub-category-list"
              className="addoutlet-btn-secondary"
            >
              Back
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubCategoryLayer;
